/**
 * TOADS product page enhancements.
 *
 * Two jobs, both progressive — the page is fully usable if this never runs:
 *
 * 1. Turn the description's headings into collapsible sections. The client
 *    writes long, genuinely useful copy (features, fit by age, care) and it
 *    arrives as one HTML blob from the product description. Rather than ask
 *    them to restructure it into separate fields, this finds each <h3> and
 *    folds the content after it into a <details>. First one open.
 *
 * 2. A sticky add-to-cart bar on mobile, which appears once the real button
 *    has scrolled away. It does not reimplement anything — it forwards a
 *    click to Horizon's own button, so variant selection, cart drawer and
 *    validation all keep working exactly as they do.
 *
 * --------------------------------------------------------------------------
 * A NOTE ON WHY THERE ARE NO MutationObservers IN HERE ANY MORE.
 *
 * Both of the "keep this in step with the selected variant" jobs below used to
 * be driven by a MutationObserver watching .product-information with
 * `subtree: true, childList: true`. Both handlers then wrote text INTO that
 * same subtree. Setting `el.textContent` replaces the element's child text node
 * whether or not the string changed, so every write emitted a childList record,
 * which re-entered the handler, which wrote again. Observer callbacks are
 * microtasks, so the loop never yielded to the event loop: the tab locked up
 * solid the instant anything in the product column mutated — which is exactly
 * what happens when you pick a size.
 *
 * It looked like "the size buttons don't do anything", and it only happened on
 * some products, because the SKU handler returned early when a product had no
 * style code to write and so never started the loop.
 *
 * Variant selection already emits a `change` event that bubbles, and Shopify
 * re-renders the section on top of that. Those two signals are sufficient and
 * they cannot feed back into themselves. If you ever need an observer here,
 * disconnect it around your own writes and compare before writing.
 * -------------------------------------------------------------------------- */
(() => {
  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

  /* Write only when the value actually changes. Cheap, and it is the guard
     that stops a re-render loop from ever starting again. */
  const setText = (el, value) => {
    if (!el) return;
    if (el.textContent === value) return;
    el.textContent = value;
  };

  /* ---------- 1. description accordions ---------- */

  const buildAccordions = (root) => {
    // Don't guess at a class name. Horizon puts several elements with class
    // `rte` in the details column (the price component is one), so find the
    // one that actually contains headings — that is the description.
    const candidates = root.querySelectorAll('rte-formatter, .rte, [class*="text-block"]');
    let rte = null;
    for (const el of candidates) {
      if (el.dataset.toadsAccordion) continue;
      const hasHeadings = [...el.children].some((c) => /^H[23]$/.test(c.tagName));
      if (hasHeadings) { rte = el; break; }
    }
    if (!rte) return;

    const isHeading = (el) => /^H[23]$/.test(el.tagName);
    const headings = [...rte.children].filter(isHeading);

    // Everything before the first heading is the marketing hook and the product
    // overview. It used to sit above the panels, permanently open — 390px of
    // prose on a 375px phone, between the buy button and the first thing a
    // parent can act on. It gets its own panel, and goes first.
    const lead = [];
    for (const el of [...rte.children]) {
      if (isHeading(el)) break;
      lead.push(el);
    }

    if (!headings.length && !lead.length) return;

    rte.dataset.toadsAccordion = 'on';

    headings.forEach((h, i) => {
      const details = document.createElement('details');
      details.className = 'toads-acc';

      const summary = document.createElement('summary');
      summary.className = 'toads-acc__summary';
      summary.textContent = h.textContent.trim();
      details.appendChild(summary);

      const body = document.createElement('div');
      body.className = 'toads-acc__body';

      // Move everything between this heading and the next into the panel.
      let node = h.nextElementSibling;
      while (node && !/^H[23]$/.test(node.tagName)) {
        const next = node.nextElementSibling;
        body.appendChild(node);
        node = next;
      }
      details.appendChild(body);
      h.replaceWith(details);
    });

    /* Built after the headings, prepended before them. Doing it in this order
       matters: the heading loop moves each heading's following siblings, and
       the lead nodes sit before every heading, so they are still exactly where
       we found them by the time we come to move them. */
    if (lead.length) {
      const details = document.createElement('details');
      details.className = 'toads-acc';

      const summary = document.createElement('summary');
      summary.className = 'toads-acc__summary';
      summary.textContent = 'Description';
      details.appendChild(summary);

      const body = document.createElement('div');
      body.className = 'toads-acc__body';
      lead.forEach((n) => body.appendChild(n));
      details.appendChild(body);

      rte.prepend(details);
    }

    /* On a phone every closed panel is screen given back to the photograph and
       the size picker, so nothing opens itself. On desktop there is room, and a
       page of closed accordions reads as an empty page, so the first one opens. */
    const first = rte.querySelector('.toads-acc');
    if (first && !isMobile()) first.open = true;
  };

  /* ---------- 2. sticky add to cart ---------- */

  const realButton = () =>
    document.querySelector(
      'product-form-component button[type="submit"], .product-form button[type="submit"], add-to-cart-component button'
    );

  /* Re-queried on every sync rather than captured once: Shopify replaces the
     whole product-information section on a variant change, so a reference taken
     at build time points at a detached button within seconds of page load. */
  const syncBar = () => {
    const bar = document.querySelector('.toads-sticky-atc');
    if (!bar) return;
    const button = realButton();
    if (!button) return;

    const priceEl = document.querySelector('[class*="product-details"] [class*="price"]');
    if (priceEl) setText(bar.querySelector('.toads-sticky-atc__price'), priceEl.textContent.trim().split('\n')[0]);

    const out = bar.querySelector('.toads-sticky-atc__button');
    setText(out, button.textContent.trim() || 'Add to cart');
    out.disabled = button.disabled;
  };

  const buildStickyBar = () => {
    if (!isMobile()) return;

    const button = realButton();
    if (!button || document.querySelector('.toads-sticky-atc')) return;

    const titleEl = document.querySelector('.product-information h1, main h1');

    const bar = document.createElement('div');
    bar.className = 'toads-sticky-atc';
    bar.hidden = true;
    bar.innerHTML = `
      <div class="toads-sticky-atc__meta">
        <span class="toads-sticky-atc__name"></span>
        <span class="toads-sticky-atc__price"></span>
      </div>
      <button class="toads-sticky-atc__button" type="button"></button>`;

    bar.querySelector('.toads-sticky-atc__name').textContent = titleEl ? titleEl.textContent.trim() : '';

    // Forward to Horizon's own button. Never post to the cart ourselves, and
    // re-query it — see syncBar.
    bar.querySelector('.toads-sticky-atc__button').addEventListener('click', () => {
      const live = realButton();
      if (live) live.click();
    });

    // The bar lives on <body>, deliberately outside .product-information, so a
    // section re-render cannot take it with it.
    document.body.appendChild(bar);
    syncBar();

    // Show the bar only while the real button is off screen.
    const io = new IntersectionObserver(
      ([entry]) => {
        bar.hidden = entry.isIntersecting;
        if (!entry.isIntersecting) syncBar();
      },
      { threshold: 0 }
    );
    io.observe(button);
  };

  /* Style code follows the selected variant ------------------------------
     The reassurance block prints the first variant's SKU server-side; Horizon
     re-renders the variant picker on selection but not our block, so keep the
     code in step from the picker's own hidden input. A parent quoting the
     wrong style code in a WhatsApp exchange request is a real support cost. */
  const syncSku = () => {
    const out = document.querySelector('[data-toads-sku]');
    if (!out) return;
    const input = document.querySelector(
      'variant-picker input[name="id"], [name="id"][type="hidden"], select[name="id"]'
    );
    if (!input || !input.value) return;
    const map = window.__toadsSkuMap;
    if (map && map[input.value]) setText(out, map[input.value]);
  };

  const refresh = () => {
    syncSku();
    syncBar();
  };

  /* Horizon applies the variant asynchronously: the `change` on the radio fires
     before the hidden input[name=id] has been rewritten, so a single read on
     the next frame still sees the previous variant. It also dispatches
     `shopify:product:select` once the swap is done — that is the reliable
     signal — but the section can be replaced wholesale after it, and on a slow
     frame the input is still stale when that arrives. So re-read on a 100ms
     tick for two seconds and then stop. Bounded, single-flight, and every pass
     is a read plus a compare-then-write, so it cannot feed itself. */
  let watchUntil = 0;
  let watching = false;

  const refreshSoon = () => {
    refresh();
    watchUntil = Date.now() + 2000;
    if (watching) return;
    watching = true;
    const step = () => {
      refresh();
      if (Date.now() < watchUntil) {
        setTimeout(step, 100);
      } else {
        watching = false;
      }
    };
    setTimeout(step, 80);
  };

  const init = () => {
    buildAccordions(document);
    buildStickyBar();
    refresh();
  };

  /* One set of listeners for the life of the page. init() runs again on every
     section render, so binding inside it stacked a new listener each time. */
  if (!window.__toadsPdpBound) {
    window.__toadsPdpBound = true;

    // A size click bubbles a `change` from the radio. This is the signal the
    // observer was reaching for, without the feedback loop.
    document.addEventListener('change', (event) => {
      const t = event.target;
      if (t && t.closest && t.closest('variant-picker, .variant-option, product-form-component')) {
        refreshSoon();
      }
    });

    // Horizon's own "the variant has changed" event.
    document.addEventListener('shopify:product:select', refreshSoon);

    // Shopify re-renders sections in the editor and on variant change.
    document.addEventListener('shopify:section:load', () => init());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
